/**
 * Which URL a staff artwork step posts to (docs/features/order-to-invoice/
 * 66_views_and_frontend_artwork_update_spec.md §2–§4).
 *
 * As for every order step (`order-to-invoice-actions.ts`), the URL is never
 * built here: it is read from the view the page holds, which carries one for
 * each step this user may take now and null for the rest. Both adapters
 * resolve through this, so the mock refuses exactly what the real adapter
 * refuses, before anything is fetched.
 */

import {
  assertRelativeActionUrl,
  OrderStepNotOfferedError,
} from "./order-to-invoice-actions";
import type {
  ArtworkCommand,
  OrderArtworkView,
  PrepressArtworkCommand,
  PrepressHomeArtwork,
  QuoteArtworkView,
  StaffProof,
  StaffVersionArtwork,
} from "./artwork-staff-types";

/** A version of an order's or a quote's artwork, by id. */
export function findVersion(
  artwork: OrderArtworkView | QuoteArtworkView,
  versionId: string,
): StaffVersionArtwork | null {
  for (const line of artwork.lines) {
    const found = line.versions.find((v) => v.version_id === versionId);
    if (found) return found;
  }
  return null;
}

function requireVersion(
  artwork: OrderArtworkView | QuoteArtworkView,
  versionId: string,
): StaffVersionArtwork {
  const version = findVersion(artwork, versionId);
  if (!version) {
    throw new OrderStepNotOfferedError(`Artwork for version ${versionId}`);
  }
  return version;
}

function requireProof(
  version: StaffVersionArtwork,
  proofId: string,
): StaffProof {
  const proof = version.proofs.find((p) => p.id === proofId);
  if (!proof) throw new OrderStepNotOfferedError(`Proof ${proofId}`);
  return proof;
}

/** Where one request of a staff artwork step posts. An upload has one per part. */
export interface ArtworkRequest {
  url: string;
  /** What the request carries: a part's file, another version's file to share, or a JSON body. */
  body:
    | { kind: "file"; partKey: string; file: File }
    | { kind: "share"; partKey: string; fromVersionId: string }
    | { kind: "whole_file"; file: File }
    | { kind: "json"; json: unknown };
}

/** The requests `command` makes on `artwork`, in order; throws when the read offers a step none. */
export function artworkRequests(
  artwork: OrderArtworkView | QuoteArtworkView,
  command: ArtworkCommand,
): ArtworkRequest[] {
  const version = requireVersion(artwork, command.versionId);
  switch (command.kind) {
    case "upload_files": {
      if (command.files.length === 0) {
        throw new OrderStepNotOfferedError("Upload with no file");
      }
      return command.files.map((choice): ArtworkRequest => {
        const part = version.parts.find((p) => p.part_key === choice.partKey);
        if ("file" in choice) {
          if (!part?.upload_url) throw new OrderStepNotOfferedError("Upload");
          return {
            url: assertRelativeActionUrl(part.upload_url),
            body: { kind: "file", partKey: choice.partKey, file: choice.file },
          };
        }
        if (!part?.share_url) {
          throw new OrderStepNotOfferedError("Share a file");
        }
        return {
          url: assertRelativeActionUrl(part.share_url),
          body: {
            kind: "share",
            partKey: choice.partKey,
            fromVersionId: choice.shareFromVersionId,
          },
        };
      });
    }
    case "decide_proof": {
      const url = requireProof(version, command.proofId).decide_url;
      if (!url) throw new OrderStepNotOfferedError("Deciding for the customer");
      return [
        {
          url: assertRelativeActionUrl(url),
          body: { kind: "json", json: command.input },
        },
      ];
    }
    case "reopen_proof": {
      const url = requireProof(version, command.proofId).reopen_url;
      if (!url) throw new OrderStepNotOfferedError("Reopen");
      return [
        {
          url: assertRelativeActionUrl(url),
          body: { kind: "json", json: command.input },
        },
      ];
    }
    case "resend_proof": {
      const url = requireProof(version, command.proofId).email_now_url;
      if (!url) throw new OrderStepNotOfferedError("Resend proof");
      // A command body (object contract): no fields but an optional request id, which Classic does not send.
      return [
        { url: assertRelativeActionUrl(url), body: { kind: "json", json: {} } },
      ];
    }
    case "decide_finding": {
      const finding = version.parts
        .flatMap((p) => p.findings)
        .find((f) => f.id === command.findingId);
      if (!finding?.decide_url) {
        throw new OrderStepNotOfferedError("Deciding a check warning");
      }
      return [
        {
          url: assertRelativeActionUrl(finding.decide_url),
          body: { kind: "json", json: command.input },
        },
      ];
    }
    case "accept_without_proof": {
      if (!version.accept_without_proof_url) {
        throw new OrderStepNotOfferedError("Accepting for the customer");
      }
      return [
        {
          url: assertRelativeActionUrl(version.accept_without_proof_url),
          body: { kind: "json", json: command.input },
        },
      ];
    }
    case "upload_whole_file": {
      if (!version.combined_upload_url) {
        throw new OrderStepNotOfferedError("Upload one file for every part");
      }
      return [
        {
          url: assertRelativeActionUrl(version.combined_upload_url),
          body: { kind: "whole_file", file: command.file },
        },
      ];
    }
    case "split_combined_file": {
      const url = version.pending_combined_file?.split_url;
      if (!url) throw new OrderStepNotOfferedError("Split into files");
      return [
        {
          url: assertRelativeActionUrl(url),
          body: { kind: "json", json: command.input },
        },
      ];
    }
    case "discard_combined_file": {
      const url = version.pending_combined_file?.discard_url;
      if (!url) throw new OrderStepNotOfferedError("Cancel the page tags");
      return [
        { url: assertRelativeActionUrl(url), body: { kind: "json", json: {} } },
      ];
    }
  }
}

/** Where a Prepress home step posts, from the list row it is about. */
export function prepressArtworkUrl(
  lists: PrepressHomeArtwork,
  command: PrepressArtworkCommand,
): string {
  switch (command.kind) {
    case "resolve_orphan": {
      const url = lists.orphans.items.find(
        (o) => o.id === command.arrivalId,
      )?.resolve_url;
      if (!url) throw new OrderStepNotOfferedError("Resolve");
      return assertRelativeActionUrl(url);
    }
    case "discard_orphan": {
      const url = lists.orphans.items.find(
        (o) => o.id === command.arrivalId,
      )?.discard_url;
      if (!url) throw new OrderStepNotOfferedError("Discard");
      return assertRelativeActionUrl(url);
    }
    case "decide_finding": {
      const url = lists.check_warnings.items.find(
        (f) => f.id === command.findingId,
      )?.decide_url;
      if (!url) throw new OrderStepNotOfferedError("Deciding a check warning");
      return assertRelativeActionUrl(url);
    }
    case "decide_item_proof": {
      const url = lists.item_proofs.items.find((p) => p.id === command.proofId)
        ?.proof.decide_url;
      if (!url) throw new OrderStepNotOfferedError("Deciding an item proof");
      return assertRelativeActionUrl(url);
    }
  }
}
